import React, { useEffect, useState } from "react";
import { Bounce, toast } from "react-toastify";
import api from "../api";
import { useNavigate } from "react-router-dom";
import CustomSelect from "../CustomSelect";
import useTrainerSearch from "../hooks/useTrainerSearch";
import useRicercaSearch from "../hooks/useRicercaSearch";

const AddAssociazioni = () => {
  const [trainerId, setTrainerId] = useState("");
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [selectedRicerca, setSelectedRicerca] = useState(null);
  const [ricercaId, setRicercaId] = useState("");
  const [note, setNote] = useState("");
  const [inputErrors, setInputErrors] = useState({});
  const [isAuthorized, setIsAuthorized] = useState(false);

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const loadTrainerOptions = useTrainerSearch();
  const loadRicercaOptions = useRicercaSearch();

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");

    if (userRole !== "ADMIN") {
      alert("Accedi come amministratore per modificare le associazioni!");

      navigate("/login");
      localStorage.clear();
      return;
    } else {
      setIsAuthorized(true);
    }
  }, [navigate]);

  const isValid = () => {
    const errors = {};

    if (!trainerId) {
      errors.trainerId = "Seleziona un docente!";
    }

    if (!ricercaId) {
      errors.ricercaId = "Seleziona una ricerca!";
    }

    setInputErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const addAssociazione = async () => {
    if (!isValid()) return;

    try {
      setLoading(true);
      const response = await api.post("/associazioni", {
        trainerId,
        ricercaId,
        note,
      });

      if (response.status === 200) {
        setTrainerId("");
        setSelectedTrainer(null);
        setRicercaId("");
        setSelectedRicerca(null);
        setNote("");
        setInputErrors({});
        toast.success("Associazione aggiunta con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthorized) return null;

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="page-header">
        <div className="page-title">
          <i className="fa-solid fa-plus-circle"></i>
          Aggiungi Associazione
        </div>
        <div className="controls">
          <button
            onClick={() => navigate("/associazioni")}
            className="back-btn"
          >
            <i className="fa-solid fa-arrow-left"></i>
            Torna alle Associazioni
          </button>
        </div>
      </div>

      <div className="page-content associazioni">
        <div className="form-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fa-solid fa-link"></i>
              Dettagli Associazione
            </div>
            <div className="card-subtitle">
              Compila i campi per creare una nuova associazione tra docente e
              ricerca
            </div>
          </div>

          <div className="card-content">
            <div className="form-grid">
              <div className="form-field">
                <label className="form-label">
                  <i className="fa-solid fa-user-tie"></i>
                  Docente
                  <span className="required">*</span>
                </label>
                <div className="form-input">
                  <CustomSelect
                    loadOptions={loadTrainerOptions}
                    value={selectedTrainer}
                    onChange={(selected) => {
                      setSelectedTrainer(selected);
                      const trainer = selected?.trainer;
                      setTrainerId(trainer?.id ?? "");
                      if (inputErrors.trainerId) {
                        setInputErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.trainerId;
                          return newErrors;
                        });
                      }
                    }}
                    placeholder="Seleziona un docente"
                    className="modern-select"
                  />
                  {inputErrors.trainerId && (
                    <span className="error-message">
                      <i className="fa-solid fa-exclamation-circle"></i>
                      {inputErrors.trainerId}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">
                  <i className="fa-solid fa-search"></i>
                  Ricerca
                  <span className="required">*</span>
                </label>
                <div className="form-input">
                  <CustomSelect
                    loadOptions={loadRicercaOptions}
                    value={selectedRicerca}
                    onChange={(selected) => {
                      setSelectedRicerca(selected);
                      setRicercaId(selected?.ricerca?.id || selected?.value || "");
                      if (inputErrors.ricercaId) {
                        setInputErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.ricercaId;
                          return newErrors;
                        });
                      }
                    }}
                    placeholder="Seleziona una ricerca"
                    className="modern-select"
                  />
                  {inputErrors.ricercaId && (
                    <span className="error-message">
                      <i className="fa-solid fa-exclamation-circle"></i>
                      {inputErrors.ricercaId}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field full-width">
                <label className="form-label">
                  <i className="fa-solid fa-sticky-note"></i>
                  Note
                </label>
                <div className="form-input">
                  <textarea
                    id="note"
                    value={note}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNote(value);
                      if (inputErrors.note && value.trim().length >= 2) {
                        setInputErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.note;
                          return newErrors;
                        });
                      }
                    }}
                    className="modern-textarea"
                    placeholder="Inserisci le note per questa associazione..."
                    rows={4}
                  />
                  {inputErrors.note && (
                    <span className="error-message">
                      <i className="fa-solid fa-exclamation-circle"></i>
                      {inputErrors.note}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                className="btn-secondary"
                onClick={() => navigate("/associazioni")}
              >
                <i className="fa-solid fa-times"></i>
                Annulla
              </button>
              <button className="btn-primary" onClick={addAssociazione}>
                <i className="fa-solid fa-save"></i>
                Aggiungi Associazione
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAssociazioni;
